import {
  createWorkbook,
  addWorksheet,
  setHeaders,
  setColumnsAndRows,
  saveWorkbook,
  DEFAULTNUMHEADERS,
  numberFormat,
  currencyFormat,
  styleTotalRow,
} from '../../utilities/helpers'
import { formatReportDate } from './fnbEodReportUtils'

function buildHeaderLines(reportData) {
  const header = reportData?.header || {}
  return [
    `Event: ${header.eventName || '—'}`,
    `Date: ${formatReportDate(header.reportDate)}`,
    `Location: ${header.venueName || '—'}`,
    `Stand: ${header.standName || '—'}`,
    `Manager: ${header.managerName || '—'}`,
  ]
}

function inventoryCell(value) {
  if (value == null || Number.isNaN(Number(value))) return ''
  return Number(value)
}

function formatVariancePercent(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(1)}%`
}

function addKeyValueSheet({
  workbook,
  sheetName,
  reportTitle,
  organizationName,
  headerLines,
  rows,
}) {
  const worksheet = addWorksheet(sheetName, workbook)
  setHeaders(reportTitle, organizationName, worksheet, headerLines)

  setColumnsAndRows(['label', 'value'], rows, (row) => row, worksheet)

  const headerRows = DEFAULTNUMHEADERS + headerLines.length
  worksheet.getColumn(1).width = 34
  worksheet.getColumn(2).width = 28

  return { worksheet, headerRows }
}

function addTableSheet({
  workbook,
  sheetName,
  reportTitle,
  organizationName,
  headerLines,
  columns,
  rows,
  formatter,
  formats = [],
}) {
  const worksheet = addWorksheet(sheetName, workbook)
  setHeaders(reportTitle, organizationName, worksheet, headerLines)

  setColumnsAndRows(columns, rows, formatter, worksheet)

  const headerRows = DEFAULTNUMHEADERS + headerLines.length
  formats.forEach(({ column, type }) => {
    if (type === 'number') numberFormat(column, worksheet, headerRows)
    if (type === 'currency') currencyFormat(column, worksheet, headerRows)
  })

  return { worksheet, headerRows }
}

export async function exportFnbEodReportToExcel(reportData, organizationName) {
  if (!reportData?.meta?.hasData) return

  const workbook = createWorkbook()
  const headerLines = buildHeaderLines(reportData)
  const summary = reportData.summary || {}
  const standMetrics = reportData.standMetrics || {}
  const paymentSummary = reportData.paymentSummary || {}

  addKeyValueSheet({
    workbook,
    sheetName: 'Report Info',
    reportTitle: 'F&B End of Day',
    organizationName,
    headerLines,
    rows: [
      { label: 'Event', value: reportData.header?.eventName || '—' },
      {
        label: 'Report Date',
        value: formatReportDate(reportData.header?.reportDate),
      },
      { label: 'Location', value: reportData.header?.venueName || '—' },
      { label: 'Stand', value: reportData.header?.standName || '—' },
      { label: 'Manager', value: reportData.header?.managerName || '—' },
      { label: 'Timezone', value: reportData.timezone || '—' },
      {
        label: 'Checked In Attendees',
        value: reportData.meta?.checkedInCount ?? 0,
      },
    ],
  })

  addTableSheet({
    workbook,
    sheetName: 'Inventory and Sales',
    reportTitle: 'Inventory and Sales',
    organizationName,
    headerLines,
    columns: [
      'item',
      'beginning_inventory',
      'received_today',
      'available',
      'sold',
      'complimentary',
      'waste',
      'ending_inventory',
      'unit_price',
      'gross_sales',
    ],
    rows: reportData.items || [],
    formatter: (item) => ({
      item: item.itemName,
      beginning_inventory: inventoryCell(item.beginningInventory),
      received_today: inventoryCell(item.receivedToday),
      available: inventoryCell(item.available),
      sold: inventoryCell(item.sold),
      complimentary: inventoryCell(item.complimentary),
      waste: inventoryCell(item.waste),
      ending_inventory: inventoryCell(item.endingInventory),
      unit_price: Number(item.unitPrice ?? 0),
      gross_sales: Number(item.grossSales ?? 0),
    }),
    formats: [
      { column: 2, type: 'number' },
      { column: 3, type: 'number' },
      { column: 4, type: 'number' },
      { column: 5, type: 'number' },
      { column: 6, type: 'number' },
      { column: 7, type: 'number' },
      { column: 8, type: 'number' },
      { column: 9, type: 'currency' },
      { column: 10, type: 'currency' },
    ],
  })

  addKeyValueSheet({
    workbook,
    sheetName: 'Summary',
    reportTitle: 'Summary',
    organizationName,
    headerLines,
    rows: [
      {
        label: 'Total Gross Sales',
        value: Number(summary.totalGrossSales ?? 0),
      },
      {
        label: 'Complimentary Items',
        value: Number(summary.complimentaryItems ?? 0),
      },
      { label: 'Waste / Spoilage', value: Number(summary.wasteSpoilage ?? 0) },
      { label: 'Total Units Sold', value: Number(summary.totalUnitsSold ?? 0) },
      {
        label: 'Average Transaction Value',
        value: Number(summary.averageTransactionValue ?? 0),
      },
      {
        label: 'Transactions Processed',
        value: Number(summary.transactionsProcessed ?? 0),
      },
    ],
  })

  const varianceRows = (reportData.variance || []).map((row) => ({
    item: row.itemName,
    expected_ending: Number(row.expectedEnding ?? 0),
    actual_count: Number(row.actualCount ?? 0),
    variance: Number(row.variance ?? 0),
    variance_percent: formatVariancePercent(row.variancePercent),
  }))

  if (!varianceRows.length) {
    varianceRows.push({
      item:
        reportData.varianceCountsRecorded && reportData.varianceAllMatched
          ? 'All recorded closing counts match expected ending inventory.'
          : reportData.varianceAvailable
            ? 'No closing counts recorded yet.'
            : 'Variance reporting requires stand inventory setup.',
      expected_ending: '',
      actual_count: '',
      variance: '',
      variance_percent: '',
    })
  }

  addTableSheet({
    workbook,
    sheetName: 'Variance Report',
    reportTitle: 'Variance Report',
    organizationName,
    headerLines,
    columns: [
      'item',
      'expected_ending',
      'actual_count',
      'variance',
      'variance_percent',
    ],
    rows: varianceRows,
    formatter: (row) => row,
    formats: [
      { column: 2, type: 'number' },
      { column: 3, type: 'number' },
      { column: 4, type: 'number' },
    ],
  })

  const paymentRows = [...(paymentSummary.payments || [])]
  if (paymentRows.length) {
    paymentRows.push({
      paymentMethod: 'total',
      label: 'Total',
      amount: Number(paymentSummary.total ?? 0),
    })
  }

  const { worksheet: paymentSheet } = addTableSheet({
    workbook,
    sheetName: 'Payment Summary',
    reportTitle: 'Payment Summary',
    organizationName,
    headerLines,
    columns: ['payment_type', 'amount'],
    rows: paymentRows.length
      ? paymentRows
      : [{ paymentMethod: 'none', label: 'No payments recorded', amount: 0 }],
    formatter: (row) => ({
      payment_type: row.label || row.paymentMethod || 'Other',
      amount: Number(row.amount ?? 0),
    }),
    formats: [{ column: 2, type: 'currency' }],
  })

  if (paymentRows.length) {
    styleTotalRow(paymentSheet.lastRow, paymentSheet, false)
  }

  const topSelling = standMetrics.topSellingItem
  const highestRevenue = standMetrics.highestRevenueItem

  addKeyValueSheet({
    workbook,
    sheetName: 'Stand Metrics',
    reportTitle: 'Stand Performance Metrics',
    organizationName,
    headerLines,
    rows: [
      {
        label: 'Revenue Per Attendee',
        value:
          standMetrics.revenuePerAttendee == null
            ? '—'
            : Number(standMetrics.revenuePerAttendee),
      },
      {
        label: 'Average Items Per Order',
        value:
          standMetrics.averageItemsPerOrder == null
            ? '—'
            : Number(standMetrics.averageItemsPerOrder),
      },
      {
        label: 'Top Selling Item',
        value: topSelling
          ? `${topSelling.name} (${topSelling.quantitySold} sold)`
          : '—',
      },
      {
        label: 'Highest Revenue Item',
        value: highestRevenue
          ? `${highestRevenue.name} ($${Number(highestRevenue.grossSales).toFixed(2)})`
          : '—',
      },
      {
        label: 'Sell Through Rate',
        value:
          standMetrics.sellThroughRate == null
            ? '—'
            : `${Number(standMetrics.sellThroughRate).toFixed(1)}%`,
      },
      {
        label: 'Waste Percentage',
        value:
          standMetrics.wastePercentage == null
            ? '—'
            : `${Number(standMetrics.wastePercentage).toFixed(1)}%`,
      },
    ],
  })

  const eventLabel = (reportData.header?.eventName || 'event')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .slice(0, 40)

  await saveWorkbook(workbook, `F&B End of Day Report - ${eventLabel}`)
}
